/**
 * Client-side photo quality heuristics, run BEFORE the paid vision call.
 *
 * This module is deliberately DOM-free: it does math on a structural
 * RGBA buffer and nothing else. The Jest environment (jsdom, no `canvas`
 * package) has no `ImageData` global and `getContext('2d')` returns null,
 * so the math must be testable from plain objects. The canvas edge lives in
 * lib/analysis/measure-photo-quality.ts.
 *
 * The output drives a SOFT warning only. It never blocks an upload and never
 * runs server-side, so every ambiguous or malformed case must fail open
 * (`ok: true`). A missed blurry photo costs one vision call; a false warning
 * costs trust on the happy path, which is roughly 50x more common.
 */

/**
 * Structural stand-in for ImageData. Never type this as ImageData: the Jest
 * env has no such global, and tests must construct these as plain objects.
 */
export interface RgbaImageLike {
  data: Uint8ClampedArray | number[]
  width: number
  height: number
}

export type PhotoQualityReason = 'blur' | 'dark' | 'bright'

export interface PhotoQualityMetrics {
  /** Variance of the 4-neighbour Laplacian over Rec.601 luma, 0-255 scale. */
  sharpness: number
  /** Mean luma, 0-255. */
  meanLuma: number
  /** Standard deviation of luma, 0-255. Reported, but NOT the blur gate. */
  lumaStdDev: number
  /**
   * Standard deviation of 8x8 block means, 0-255. Blur-invariant by
   * construction (averaging over a block larger than the blur kernel keeps the
   * low-frequency structure), which is why this and not `lumaStdDev` decides
   * whether the sharpness metric is judgeable.
   */
  coarseContrast: number
  /** Share of pixels with luma <= 8. */
  clippedDarkRatio: number
  /** Share of pixels with luma >= 247. */
  clippedBrightRatio: number
  /** Dimensions actually measured (post-downscale). */
  sampledWidth: number
  sampledHeight: number
}

export interface PhotoQualityVerdict {
  /** true means "no warning". Includes the cannot-judge case. */
  ok: boolean
  reason: PhotoQualityReason | null
  metrics: PhotoQualityMetrics
  /**
   * False when the frame carried too little coarse structure for the sharpness
   * metric to mean anything, so blur was never evaluated. Separating this from
   * a plain pass is what makes the abstain population visible in analytics.
   */
  blurJudgeable: boolean
}

/**
 * Tuned for a ~256px longest-edge measurement copy. Downscaling RAISES
 * apparent Laplacian variance (aliasing concentrates edge energy), so the
 * folk OpenCV threshold of ~100 at native resolution would over-fire here.
 *
 * Do not retune these without ~3 weeks of `capture_quality_override` ->
 * `grass_identification_outcome` data; at ~100 addressable events per 60 days
 * precision is not readable sooner.
 */
export const PHOTO_QUALITY_THRESHOLDS = {
  /**
   * In-focus turf is dense high-frequency texture and lands in the hundreds to
   * thousands at 256px; a visibly out-of-focus frame collapses below ~40.
   * 60 sits in the empty band and biases hard toward false negatives.
   */
  SHARPNESS_MIN: 60,
  /**
   * A flat wall, overcast sky, or solid driveway has near-zero Laplacian
   * variance and is perfectly in focus. Below this contrast the sharpness
   * metric is uninformative, so we abstain rather than call it blurry.
   *
   * This gate is measured on BLOCK MEANS, not on raw luma stddev. Raw stddev is
   * destroyed by the very blur it is supposed to gate: a flat-light turf
   * close-up (the dominant poor_image case) measures ~8 sharp and ~3.9 once
   * blurred, so an 18-level raw-stddev gate suppressed the warning on exactly
   * the photo this feature exists for. Block means at 8px survive defocus
   * (~5.6 sharp vs ~3.8 blurred on the same frame, and still ~3.7 under heavy
   * defocus) while a genuinely featureless frame stays near 0.
   *
   * Accepted cost: a sharp photo of a low-detail non-turf subject (a shaded
   * wall with a lighting gradient) can now read as blurry. That subject is a
   * wrong-subject capture that would abstain anyway, and the warning is soft.
   */
  MIN_COARSE_CONTRAST_TO_JUDGE_BLUR: 2.5,
  /**
   * Below ~48 mean luma blade color, the dominant identify feature, is
   * unrecoverable. Dusk-but-usable turf shots sit at 70-120.
   */
  DARK_MEAN_MAX: 48,
  /** Catches flash-lit-subject-on-black-background, which a mean alone misses. */
  DARK_CLIP_RATIO_MAX: 0.55,
  /** Blown-out midday shots. Normal sunlit turf is 140-180. */
  BRIGHT_MEAN_MIN: 208,
  /** A third of the frame at pure white means the blade edges are gone. */
  BRIGHT_CLIP_RATIO_MAX: 0.32,
  /**
   * The clip ratio alone is not evidence of overexposure. A standing homeowner
   * shooting the front yard puts the horizon near mid-frame, and phone metering
   * routinely clips overcast sky to 250+, so a correctly exposed lawn with 40%
   * sky measures clip 0.40 at mean 163 — sharp, usable, and "washed out" is
   * wrong advice for it. The clip branch therefore only fires when the WHOLE
   * frame is also bright, which a sky band alone cannot produce.
   */
  BRIGHT_CLIP_MEAN_MIN: 190,
} as const

/** Luma at or below this counts as crushed black. */
const DARK_CLIP_LEVEL = 8
/** Luma at or above this counts as blown white. */
const BRIGHT_CLIP_LEVEL = 247

/** Edge of the square block the coarse-contrast statistic averages over. */
const COARSE_BLOCK = 8

const ZERO_METRICS: PhotoQualityMetrics = {
  sharpness: 0,
  meanLuma: 0,
  lumaStdDev: 0,
  coarseContrast: 0,
  clippedDarkRatio: 0,
  clippedBrightRatio: 0,
  sampledWidth: 0,
  sampledHeight: 0,
}

function cannotJudge(): PhotoQualityVerdict {
  return { ok: true, reason: null, metrics: { ...ZERO_METRICS }, blurJudgeable: false }
}

/**
 * Standard deviation of block means. Averaging over a block wider than a
 * realistic defocus kernel makes this near-invariant to blur, so it measures
 * "does this frame have any structure at all" without being wiped out by the
 * condition it gates.
 */
function coarseContrastOf(luma: Float32Array, width: number, height: number): number {
  const blocksX = Math.ceil(width / COARSE_BLOCK)
  const blocksY = Math.ceil(height / COARSE_BLOCK)
  const blockCount = blocksX * blocksY
  if (blockCount < 4) return 0

  const sums = new Float64Array(blockCount)
  const counts = new Float64Array(blockCount)
  for (let y = 0; y < height; y++) {
    const blockRow = Math.floor(y / COARSE_BLOCK) * blocksX
    for (let x = 0; x < width; x++) {
      const b = blockRow + Math.floor(x / COARSE_BLOCK)
      sums[b] += luma[y * width + x]
      counts[b]++
    }
  }

  let meanSum = 0
  let meanSquareSum = 0
  for (let b = 0; b < blockCount; b++) {
    const mean = counts[b] > 0 ? sums[b] / counts[b] : 0
    meanSum += mean
    meanSquareSum += mean * mean
  }
  const mean = meanSum / blockCount
  return Math.sqrt(Math.max(0, meanSquareSum / blockCount - mean * mean))
}

/**
 * Evaluate a downscaled RGBA buffer for blur and exposure problems.
 *
 * Never throws. Any malformed input returns `{ ok: true, reason: null }`.
 */
export function evaluateImageQuality(image: RgbaImageLike): PhotoQualityVerdict {
  try {
    if (!image) return cannotJudge()

    const width = Math.floor(image.width)
    const height = Math.floor(image.height)
    const data = image.data

    if (
      !Number.isFinite(width) ||
      !Number.isFinite(height) ||
      width < 3 ||
      height < 3 ||
      !data ||
      typeof data.length !== 'number' ||
      data.length < width * height * 4
    ) {
      return cannotJudge()
    }

    const pixelCount = width * height
    const luma = new Float32Array(pixelCount)

    let lumaSum = 0
    let lumaSquareSum = 0
    let darkClipped = 0
    let brightClipped = 0

    // A Uint8ClampedArray is 0..255 by construction; a plain number[] (test
    // fixtures, hostile callers) can carry NaN or out-of-range values that
    // would corrupt every statistic downstream, so those inputs abstain.
    const needsRangeCheck =
      typeof Uint8ClampedArray === 'undefined' || !(data instanceof Uint8ClampedArray)

    // Pass 1: Rec.601 luma, running moments, and the two clip counters.
    // Alpha is ignored entirely; a transparent pixel is judged on its RGB.
    for (let i = 0; i < pixelCount; i++) {
      const o = i * 4
      if (needsRangeCheck) {
        const r = data[o]
        const g = data[o + 1]
        const b = data[o + 2]
        // NaN fails every comparison, so it lands here too.
        if (!(r >= 0 && r <= 255 && g >= 0 && g <= 255 && b >= 0 && b <= 255)) {
          return cannotJudge()
        }
      }
      const y = 0.299 * data[o] + 0.587 * data[o + 1] + 0.114 * data[o + 2]
      luma[i] = y
      lumaSum += y
      lumaSquareSum += y * y
      if (y <= DARK_CLIP_LEVEL) darkClipped++
      if (y >= BRIGHT_CLIP_LEVEL) brightClipped++
    }

    const meanLuma = lumaSum / pixelCount
    const lumaVariance = Math.max(0, lumaSquareSum / pixelCount - meanLuma * meanLuma)
    const lumaStdDev = Math.sqrt(lumaVariance)
    const clippedDarkRatio = darkClipped / pixelCount
    const clippedBrightRatio = brightClipped / pixelCount

    // Pass 2: 4-neighbour Laplacian over interior pixels only.
    let lapSum = 0
    let lapSquareSum = 0
    let lapCount = 0
    for (let y = 1; y < height - 1; y++) {
      const row = y * width
      for (let x = 1; x < width - 1; x++) {
        const i = row + x
        const lap =
          4 * luma[i] - luma[i - width] - luma[i + width] - luma[i - 1] - luma[i + 1]
        lapSum += lap
        lapSquareSum += lap * lap
        lapCount++
      }
    }

    const lapMean = lapCount > 0 ? lapSum / lapCount : 0
    const sharpness =
      lapCount > 0 ? Math.max(0, lapSquareSum / lapCount - lapMean * lapMean) : 0

    const coarseContrast = coarseContrastOf(luma, width, height)

    const metrics: PhotoQualityMetrics = {
      sharpness,
      meanLuma,
      lumaStdDev,
      coarseContrast,
      clippedDarkRatio,
      clippedBrightRatio,
      sampledWidth: width,
      sampledHeight: height,
    }

    if (!Number.isFinite(meanLuma) || !Number.isFinite(sharpness)) {
      return cannotJudge()
    }

    // Exposure wins over blur. A mean is an unambiguous measurement while a
    // variance threshold is a heuristic, AND sensor noise in an underexposed
    // frame inflates Laplacian variance, so a dark photo can score as sharp.
    // Only one warning ever shows, and the remedy copy differs.
    const t = PHOTO_QUALITY_THRESHOLDS
    const blurJudgeable = coarseContrast >= t.MIN_COARSE_CONTRAST_TO_JUDGE_BLUR

    if (meanLuma < t.DARK_MEAN_MAX || clippedDarkRatio > t.DARK_CLIP_RATIO_MAX) {
      return { ok: false, reason: 'dark', metrics, blurJudgeable }
    }
    if (
      meanLuma > t.BRIGHT_MEAN_MIN ||
      (clippedBrightRatio > t.BRIGHT_CLIP_RATIO_MAX && meanLuma >= t.BRIGHT_CLIP_MEAN_MIN)
    ) {
      return { ok: false, reason: 'bright', metrics, blurJudgeable }
    }
    if (blurJudgeable && sharpness < t.SHARPNESS_MIN) {
      return { ok: false, reason: 'blur', metrics, blurJudgeable }
    }

    return { ok: true, reason: null, metrics, blurJudgeable }
  } catch {
    return cannotJudge()
  }
}
